import { Button, Tooltip } from "antd";
import moment from "moment";
import { useState } from "react";
import { BiTransfer } from "react-icons/bi";
import { HiCheck } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import KitsTransformModal from "../kits-modals/KitsTransferModal";
import OperatorEditModal from "../kits-modals/OperatorEditModal";
import { getKitLabelWithoutCount } from "@src/helpers/functions";
import { deviceImages, getDeviceTypes } from "@src/helpers/constants";
import { useCheckPageComponentAccess } from "@src/hooks/index";

type Props = {
  kit: any;
};

const tr = (t: any, key: string, fallback: string) => {
  const v = t(key);
  return v && v !== key ? v : fallback;
};

const getValidity = (date: any, t: any) => {
  if (!date) {
    return { state: "neutral", label: tr(t, "no_expiry", "No expiry") };
  }
  const now = moment();
  const end = moment(date);
  const days = end.diff(now, "days");

  if (end.isBefore(now)) return { state: "expired", label: tr(t, "expired", "Expired") };
  if (days <= 30) return { state: "warning", label: tr(t, "expiring_soon", "Expiring") };
  return { state: "active", label: tr(t, "active", "Active") };
};

const KitsDetailsCard = (props: Props) => {
  const { kit } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState("");
  const { t } = useTranslation();

  const canUpdateOperator = useCheckPageComponentAccess("update_operator_action");
  const canSetOperator = useCheckPageComponentAccess("set_operator_action");
  const canTransfer = useCheckPageComponentAccess("transfer_kit_action");

  const onClickToTransfer = (_id: any) => {
    setModalOpen(true);
    setSelectedId(_id);
  };

  const onClickEditProfile = () => {
    setEditModalOpen(true);
  };

  const filterDevicesByType = (type: string) =>
    kit.kitDeviceList?.filter((d: any) => d.device.type === type) || [];

  const deviceTypeKeys = Object.keys(kit).filter(
    (key: any) => key.startsWith("maximum") && kit[key] > 0
  );

  const totalSlots = deviceTypeKeys.reduce(
    (sum: number, k: string) => sum + (kit[k] || 0),
    0
  );
  const filledSlots = deviceTypeKeys.reduce(
    (sum: number, k: string) =>
      sum + filterDevicesByType(getDeviceTypes[k]).length,
    0
  );

  const validity = getValidity(kit?.effectiveTill, t);

  return (
    <div className="kit-card-wrapper">
      <div className={`kit-card kit-card--${validity.state}`}>
        <div className="kit-accent" />

        <div className="kit-top">
          <div className="kit-top-main">
            <div className="kit-title-row">
              <h3 className="kit-title">{kit.referenceId}</h3>
              <span className={`kit-status kit-status--${validity.state}`}>
                <span className="kit-status-dot" />
                {validity.label}
              </span>
            </div>
            <div className="kit-meta">
              <span className="kit-meta-item">
                {kit?.organization?.name || "—"}
              </span>
              {kit?.effectiveTill && (
                <>
                  <span className="kit-meta-sep">·</span>
                  <span className="kit-meta-item">
                    {tr(t, "valid_till", "Valid till")}{" "}
                    <strong>
                      {moment(kit.effectiveTill).format("DD MMM YYYY")}
                    </strong>
                  </span>
                </>
              )}
              <span className="kit-meta-sep">·</span>
              <span className="kit-meta-item kit-meta-item--slots">
                <strong>
                  {filledSlots}/{totalSlots}
                </strong>{" "}
                {tr(t, "devices", "devices")}
              </span>
            </div>
          </div>

          <div className="kit-top-side">
            {kit.operator?.name ? (
              <Tooltip
                title={
                  canUpdateOperator
                    ? tr(t, "edit_operator", "Edit operator")
                    : kit.operator.name
                }
              >
                <div
                  className={`kit-operator ${
                    canUpdateOperator ? "kit-operator--clickable" : ""
                  }`}
                  onClick={canUpdateOperator ? onClickEditProfile : undefined}
                >
                  <div className="kit-operator-avatar">
                    {kit.operator?.name?.charAt(0)?.toUpperCase() || "O"}
                  </div>
                  <div className="kit-operator-text">
                    <span className="kit-operator-label">
                      {tr(t, "operator", "Operator")}
                    </span>
                    <span className="kit-operator-name">
                      {kit.operator?.name}
                    </span>
                  </div>
                </div>
              </Tooltip>
            ) : canSetOperator ? (
              <Button
                onClick={onClickEditProfile}
                className="kit-set-operator-btn"
                size="small"
              >
                + {tr(t, "set_operator", "Set operator")}
              </Button>
            ) : null}

            {canTransfer && (
              <Tooltip title={tr(t, "transfer_kit", "Transfer kit")}>
                <button
                  type="button"
                  className="kit-transfer-btn"
                  onClick={() => onClickToTransfer(kit?.id)}
                  aria-label="Transfer kit"
                >
                  <BiTransfer size={16} />
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        <ul className="kit-devices">
          {deviceTypeKeys.map((item: any, idx: any) => {
            const deviceType = getDeviceTypes[item];
            const devicesByType = filterDevicesByType(deviceType);
            const maxCount = kit[item] || 0;
            const filledCount = devicesByType.length;
            const isFilled = filledCount > 0;
            const device = devicesByType[0];
            const extra = devicesByType.length - 1;

            return (
              <li
                key={idx}
                className={`kit-device ${
                  isFilled ? "kit-device--filled" : "kit-device--empty"
                }`}
              >
                <div className="kit-device-icon">
                  <img
                    src={deviceImages?.[deviceType] || ""}
                    alt={deviceType}
                  />
                </div>

                <div className="kit-device-main">
                  <span className="kit-device-type">
                    {getKitLabelWithoutCount(item, kit)}
                  </span>
                  {isFilled ? (
                    <Tooltip
                      title={
                        extra > 0
                          ? devicesByType
                              .map(
                                (d: any) =>
                                  `${d.device.referenceId} · ${d.device.macAddress}`
                              )
                              .join("\n")
                          : `${device.device.referenceId} · ${device.device.macAddress}`
                      }
                    >
                      <span className="kit-device-info">
                        <span className="kit-device-ref">
                          {device.device.referenceId}
                        </span>
                        <span className="kit-device-mac">
                          {device.device.macAddress}
                        </span>
                        {extra > 0 && (
                          <span className="kit-device-extra">+{extra}</span>
                        )}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="kit-device-info kit-device-info--empty">
                      {tr(t, "no_devices_added", "No device assigned")}
                    </span>
                  )}
                </div>

                <div className="kit-device-end">
                  {isFilled ? (
                    <span className="kit-device-check">
                      <HiCheck size={12} />
                    </span>
                  ) : (
                    <span className="kit-device-empty-dot" />
                  )}
                  <span className="kit-device-count">
                    {filledCount}/{maxCount}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <KitsTransformModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        selectedId={selectedId}
      />
      <OperatorEditModal
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        item={kit}
      />
    </div>
  );
};

export default KitsDetailsCard;
